/**
 * SEO + structure extras for every /products/:slug page:
 *  - seoTitle / seoDescription: unique, search-intent-based metadata
 *  - workflow: the "Real workflow" process diagram
 *  - mockups: which dashboard previews to show (see components/ProductMockups)
 *  - related: related product/feature slugs (internal links)
 *  - industries: relevant industry slugs (internal links)
 */
import {
  Send, Users, FileText, Wand2, CalendarClock, BarChart3, MessageSquare, Bot, HelpCircle, Target, UserCheck,
  Inbox, CheckCircle2, UserPlus, Contact, MessagesSquare, Repeat, Zap, Filter, Timer, LogOut, Link2, ShieldCheck,
  BadgeCheck, ClipboardCheck, FileSearch, PhoneIncoming, Headphones, ListChecks, Webhook, Star, Store, Share2,
  Instagram, Megaphone, MousePointerClick, Sparkles, Image as ImageIcon, PenLine, ShoppingBag, Package, ShoppingCart,
  Truck, Sheet, Droplets, Play, QrCode, Printer, ScanLine,
} from "lucide-react";

export const PRODUCT_EXTRAS = {
  "whatsapp-business-api": {
    seoTitle: "WhatsApp Business API Provider – Meta Tech Provider | GrabNChat",
    seoDescription: "Get the official WhatsApp Business API with GrabNChat, a Meta Tech Provider. Embedded Signup, template approvals, multi-agent inbox, REST API and webhooks.",
    short: "WhatsApp Business API",
    workflow: [
      { title: "Sign up", desc: "Create your GrabNChat account", icon: UserPlus },
      { title: "Embedded Signup", desc: "Connect your number via Meta", icon: Link2 },
      { title: "Verify business", desc: "Display name & profile", icon: ShieldCheck },
      { title: "Approve templates", desc: "Submit to Meta for review", icon: FileText },
      { title: "Go live", desc: "Broadcast, automate, reply", icon: Send },
    ],
    mockups: ["connect", "analytics"],
    related: ["bulk-whatsapp-marketing", "whatsapp-chatbot", "shared-team-inbox", "green-tick-verification"],
    industries: ["ecommerce", "education", "healthcare", "real-estate"],
  },
  "bulk-whatsapp-marketing": {
    seoTitle: "Bulk WhatsApp Marketing Software | GrabNChat",
    seoDescription: "Send bulk WhatsApp marketing campaigns with approved templates, audience segmentation, personalisation, scheduling and live delivery & read analytics.",
    short: "Bulk WhatsApp marketing",
    workflow: [
      { title: "Create campaign", icon: Megaphone },
      { title: "Select audience", desc: "Tags & attributes", icon: Users },
      { title: "Choose template", desc: "Meta-approved", icon: FileText },
      { title: "Personalise", desc: "Name, offer, order ID", icon: Wand2 },
      { title: "Schedule", icon: CalendarClock },
      { title: "Send", icon: Send },
      { title: "Track results", desc: "Delivered, read, replied", icon: BarChart3 },
    ],
    mockups: ["campaign", "analytics"],
    related: ["whatsapp-broadcast", "drip-campaigns", "whatsapp-crm", "whatsapp-automation"],
    industries: ["ecommerce", "retail", "education", "travel"],
  },
  "whatsapp-broadcast": {
    seoTitle: "WhatsApp Broadcast Software – Schedule & Track | GrabNChat",
    seoDescription: "Send WhatsApp broadcasts to opted-in contacts by tag or segment. Schedule sends, fill template variables and track sent, delivered, read and failed in real time.",
    short: "WhatsApp broadcasts",
    workflow: [
      { title: "Pick audience", desc: "Tag, segment or list", icon: Users },
      { title: "Choose template", icon: FileText },
      { title: "Fill variables", icon: Wand2 },
      { title: "Schedule or send now", icon: CalendarClock },
      { title: "Measure", desc: "Per-campaign report", icon: BarChart3 },
    ],
    mockups: ["campaign", "analytics"],
    related: ["bulk-whatsapp-marketing", "drip-campaigns", "whatsapp-crm", "whatsapp-chatbot"],
    industries: ["retail", "ecommerce", "education", "travel"],
  },
  "whatsapp-chatbot": {
    seoTitle: "No-Code WhatsApp Chatbot Builder | GrabNChat",
    seoDescription: "Build a WhatsApp chatbot without code. Drag-and-drop flows for FAQs, lead capture, appointment booking and qualification, with handoff to a human agent.",
    short: "WhatsApp chatbot",
    workflow: [
      { title: "Customer message", desc: "Keyword, ad click or QR", icon: MessageSquare },
      { title: "Bot response", desc: "Text, buttons, lists", icon: Bot },
      { title: "Question", desc: "Name, need, budget", icon: HelpCircle },
      { title: "Lead capture", desc: "Saved to Leads & CRM", icon: Target },
      { title: "Agent handoff", desc: "Assigned in the Inbox", icon: UserCheck },
    ],
    mockups: ["chatbot", "inbox"],
    related: ["whatsapp-automation", "shared-team-inbox", "lead-management", "whatsapp-crm"],
    industries: ["education", "healthcare", "real-estate", "travel"],
  },
  "whatsapp-automation": {
    seoTitle: "WhatsApp Automation – Auto-Replies & Journeys | GrabNChat",
    seoDescription: "Automate WhatsApp conversations with keyword auto-replies, chatbot flows, drip follow-ups and tag-based journeys that run 24/7 on the official WhatsApp API.",
    short: "WhatsApp automation",
    workflow: [
      { title: "Trigger", desc: "Keyword, tag or new lead", icon: Zap },
      { title: "Condition", desc: "Tag, attribute or reply", icon: Filter },
      { title: "Message", desc: "Template or flow", icon: Send },
      { title: "Delay", desc: "Hours or days", icon: Timer },
      { title: "Follow-up", icon: Repeat },
      { title: "Conversion", desc: "Exit & hand to sales", icon: CheckCircle2 },
    ],
    mockups: ["automation", "chatbot"],
    related: ["drip-campaigns", "whatsapp-chatbot", "lead-management", "bulk-whatsapp-marketing"],
    industries: ["ecommerce", "education", "real-estate", "retail"],
  },
  "drip-campaigns": {
    seoTitle: "WhatsApp Drip Campaigns – Automated Follow-Ups | GrabNChat",
    seoDescription: "Create WhatsApp drip campaigns: an entry template, timed follow-ups and an exit template that stops the sequence automatically when a customer converts.",
    short: "Drip campaigns",
    workflow: [
      { title: "Contact enrols", desc: "All contacts or a tag", icon: Play },
      { title: "Entry template", icon: FileText },
      { title: "Delay", desc: "Send hours & days", icon: Timer },
      { title: "Follow-up steps", icon: Repeat },
      { title: "Exit template", desc: "Contact converts", icon: LogOut },
    ],
    mockups: ["automation", "analytics"],
    related: ["whatsapp-automation", "bulk-whatsapp-marketing", "lead-management", "whatsapp-crm"],
    industries: ["education", "real-estate", "ecommerce", "healthcare"],
  },
  "shared-team-inbox": {
    seoTitle: "Shared WhatsApp Team Inbox for Multiple Agents | GrabNChat",
    seoDescription: "Let your whole team reply from one WhatsApp Business number. Assign chats to agents, tag conversations and handle WhatsApp, Instagram and Messenger in one inbox.",
    short: "Shared team inbox",
    workflow: [
      { title: "Multiple agents", desc: "Sales & support", icon: Users },
      { title: "One WhatsApp number", icon: MessageSquare },
      { title: "Assigned conversations", desc: "Owner per chat", icon: UserCheck },
      { title: "Reply with context", desc: "Tags & chat history", icon: MessagesSquare },
      { title: "Customer resolved", icon: CheckCircle2 },
    ],
    mockups: ["inbox", "crm"],
    related: ["whatsapp-crm", "whatsapp-chatbot", "whatsapp-calling", "facebook-instagram-management"],
    industries: ["ecommerce", "healthcare", "real-estate", "travel"],
  },
  "whatsapp-crm": {
    seoTitle: "WhatsApp CRM – Contacts, Tags & Chat History | GrabNChat",
    seoDescription: "A WhatsApp CRM that keeps every contact's tags, custom attributes and conversation history in one profile, so you can segment, follow up and convert more leads.",
    short: "WhatsApp CRM",
    workflow: [
      { title: "Lead", desc: "Ads, forms or chat", icon: Target },
      { title: "Customer profile", desc: "Tags & attributes", icon: Contact },
      { title: "Conversation", desc: "Full history", icon: MessagesSquare },
      { title: "Follow-up", desc: "Template or drip", icon: Repeat },
      { title: "Conversion", icon: CheckCircle2 },
    ],
    mockups: ["crm", "inbox"],
    related: ["lead-management", "shared-team-inbox", "bulk-whatsapp-marketing", "drip-campaigns"],
    industries: ["real-estate", "education", "ecommerce", "healthcare"],
  },
  "green-tick-verification": {
    seoTitle: "WhatsApp Green Tick Verification Assistance | GrabNChat",
    seoDescription: "Apply for the WhatsApp verified business badge with expert help: eligibility review, application preparation and follow-up with Meta until a decision is made.",
    short: "Green Tick",
    workflow: [
      { title: "Eligibility check", icon: FileSearch },
      { title: "Prepare application", icon: ClipboardCheck },
      { title: "Submit to Meta", icon: Send },
      { title: "Follow up", icon: Repeat },
      { title: "Decision", desc: "Made by Meta", icon: BadgeCheck },
    ],
    mockups: ["connect"],
    related: ["whatsapp-business-api", "bulk-whatsapp-marketing", "shared-team-inbox", "whatsapp-chatbot"],
    industries: ["ecommerce", "education", "healthcare", "travel"],
  },
  "whatsapp-calling": {
    seoTitle: "WhatsApp Business Calling in the Browser | GrabNChat",
    seoDescription: "Let customers call your business on WhatsApp and answer in the GrabNChat dashboard. Call permissions, callbacks, a calls list, recordings and call webhooks.",
    short: "WhatsApp calling",
    workflow: [
      { title: "Customer taps call", icon: PhoneIncoming },
      { title: "Rings in dashboard", icon: Headphones },
      { title: "Agent answers", icon: UserCheck },
      { title: "Call logged", desc: "Status & duration", icon: ListChecks },
      { title: "Webhook", desc: "To your CRM", icon: Webhook },
    ],
    mockups: ["inbox"],
    related: ["shared-team-inbox", "whatsapp-crm", "whatsapp-chatbot", "whatsapp-business-api"],
    industries: ["healthcare", "real-estate", "travel", "retail"],
  },
  "google-business-profile": {
    seoTitle: "Google Business Profile Management & Review Link | GrabNChat",
    seoDescription: "Manage your Google Business Profile from GrabNChat: edit your listing, publish posts and photos, reply to reviews and collect more ratings with a short link.",
    short: "Google Business Profile",
    workflow: [
      { title: "Connect Google", icon: Link2 },
      { title: "Update profile", icon: Store },
      { title: "Post & reply", desc: "Posts, photos, reviews", icon: PenLine },
      { title: "Share rating link", desc: "WhatsApp or QR", icon: Share2 },
      { title: "More reviews", icon: Star },
    ],
    mockups: [],
    related: ["qr-kits", "facebook-instagram-management", "ai-content-studio", "bulk-whatsapp-marketing"],
    industries: ["retail", "healthcare", "travel", "real-estate"],
  },
  "facebook-instagram-management": {
    seoTitle: "Facebook & Instagram Management Tool | GrabNChat",
    seoDescription: "Manage your Facebook Page and Instagram account in GrabNChat: posts, comment moderation, Messenger and Instagram DMs in the shared inbox, and insights.",
    short: "Facebook & Instagram management",
    workflow: [
      { title: "Connect Page & Instagram", icon: Link2 },
      { title: "Posts & comments", icon: Instagram },
      { title: "DMs in the inbox", icon: Inbox },
      { title: "Reply & moderate", icon: MessagesSquare },
      { title: "Track insights", icon: BarChart3 },
    ],
    mockups: ["inbox"],
    related: ["ads-manager", "ai-content-studio", "shared-team-inbox", "lead-management"],
    industries: ["retail", "ecommerce", "travel", "education"],
  },
  "lead-management": {
    seoTitle: "WhatsApp Lead Management & Lead Ads Sync | GrabNChat",
    seoDescription: "Collect leads from Facebook & Instagram Lead Ads, Google Sheets, your website and WhatsApp chats in one list, then follow up on WhatsApp within minutes.",
    short: "Lead management",
    workflow: [
      { title: "Lead sources", desc: "Ads, Sheets, website", icon: Sheet },
      { title: "One lead list", desc: "Stages & owners", icon: Target },
      { title: "WhatsApp follow-up", icon: Send },
      { title: "Drip nurture", icon: Droplets },
      { title: "Conversion", icon: CheckCircle2 },
    ],
    mockups: ["crm", "automation"],
    related: ["whatsapp-crm", "ads-manager", "drip-campaigns", "whatsapp-chatbot"],
    industries: ["real-estate", "education", "healthcare", "travel"],
  },
  "ads-manager": {
    seoTitle: "Click-to-WhatsApp Ads & Meta Ads Manager | GrabNChat",
    seoDescription: "Create Meta ad campaigns, boost Facebook posts and run click-to-WhatsApp ads from GrabNChat so every ad click becomes a WhatsApp conversation your team can close.",
    short: "Click-to-WhatsApp ads",
    workflow: [
      { title: "Connect ad account", icon: Link2 },
      { title: "Create or boost", icon: Megaphone },
      { title: "Ad click", desc: "Opens WhatsApp", icon: MousePointerClick },
      { title: "Chat in the inbox", icon: Inbox },
      { title: "Close the sale", icon: CheckCircle2 },
    ],
    mockups: ["inbox", "crm"],
    related: ["lead-management", "facebook-instagram-management", "whatsapp-chatbot", "shared-team-inbox"],
    industries: ["real-estate", "education", "ecommerce", "retail"],
  },
  "ai-content-studio": {
    seoTitle: "AI Social Media Post & Video Generator | GrabNChat",
    seoDescription: "Create social media images, captions and short vertical videos with AI, add your logo automatically and publish or schedule to Facebook and Instagram.",
    short: "AI posts & videos",
    workflow: [
      { title: "Describe your post", icon: PenLine },
      { title: "AI image & caption", icon: Sparkles },
      { title: "Logo added", icon: ImageIcon },
      { title: "Review & edit", icon: CheckCircle2 },
      { title: "Publish or schedule", icon: CalendarClock },
    ],
    mockups: [],
    related: ["facebook-instagram-management", "google-business-profile", "ads-manager", "bulk-whatsapp-marketing"],
    industries: ["retail", "travel", "ecommerce", "healthcare"],
  },
  "whatsapp-catalog-ecommerce": {
    seoTitle: "WhatsApp Catalog & Shopify Commerce | GrabNChat",
    seoDescription: "Manage WhatsApp product catalogs, share products and product lists in chat, and send Shopify order and shipping updates automatically on WhatsApp.",
    short: "WhatsApp catalog",
    workflow: [
      { title: "Create catalog", icon: Package },
      { title: "Add products", desc: "Photos & prices", icon: ShoppingBag },
      { title: "Share in chat", icon: MessageSquare },
      { title: "Add to cart", icon: ShoppingCart },
      { title: "Order updates", desc: "Shopify app", icon: Truck },
    ],
    mockups: ["inbox", "campaign"],
    related: ["bulk-whatsapp-marketing", "whatsapp-automation", "shared-team-inbox", "whatsapp-chatbot"],
    industries: ["ecommerce", "retail"],
  },
  "qr-kits": {
    seoTitle: "QR Kits for Vendors – WhatsApp Chat & Google Rating QR Posters | GrabNChat",
    seoDescription: "Print-ready QR posters: customers scan to chat with you on WhatsApp or rate you on Google in 10 seconds.",
    short: "QR Kits",
    workflow: [
      { title: "Choose a kit", icon: QrCode },
      { title: "Print & place", icon: Printer },
      { title: "Customer scans", icon: ScanLine },
      { title: "Chat or rating", icon: Star },
    ],
    mockups: [],
    related: ["google-business-profile", "whatsapp-chatbot", "shared-team-inbox"],
    industries: ["retail", "healthcare"],
  },
};

export const MOCKUP_CAPTIONS = {
  campaign: ["Campaign builder", "Pick an audience, choose an approved template, personalise it and schedule — then watch sent, delivered, read and failed counts."],
  analytics: ["Analytics dashboard", "Messages sent and received per day, delivery status and your best-performing templates."],
  chatbot: ["Chatbot flow builder", "Triggers, bot replies with buttons, questions, lead capture and agent handoff on one canvas."],
  inbox: ["Shared team inbox", "WhatsApp and Instagram chats in one place, each assigned to an agent with tags and history."],
  crm: ["Contact profile", "Tags, custom attributes and a timeline of every lead event and message."],
  automation: ["Drip campaign", "Entry trigger, templates, delays with send windows and an exit template that ends the sequence."],
  connect: ["Channels", "Your WhatsApp Business API number, quality rating, approved templates and API keys."],
};
